"use client";

import React from "react";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { ArticlePyramid } from "../../../partials/articles/ArticlePyramid";
import { TeamLogosScroll } from "../../../partials/teams/TeamLogosScroll";
import { MatchTabbedList } from "../../../partials/matches/MatchTabbedList";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { StandingsCompact } from "../../../partials/standings/StandingsCompact";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import { HOME_TEAMS, HOME_HERO, HOME_MATCHES, HOME_LEADERS, HOME_STATS } from "@/features/theme/sectionKeys";
import { InternationalStandingsCompact } from "@/views/standings/overrides/international/InternationalStandingsCompact";

interface FootballCompetitionHomeClassicLayoutProps {
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

export function FootballCompetitionHomeClassicLayout({
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles,
}: FootballCompetitionHomeClassicLayoutProps = {}) {
    const {
        latestNews,
        featuredPosts,
        promos,
        leaders,
        allTeams,
        allStageStandings,
        homeSlices,
        competitionStats,
        goalDistribution,
        hasCompetitionStats,
    } = useCompetitionHomeData();

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* Row 1: Teams */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_TEAMS]}>
                    <TeamLogosScroll teams={allTeams} />
                </SectionStrip>

                {/* Row 2: News and Standings */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div className="lg:col-span-2 flex flex-col">
                            <ArticlePyramid
                                articles={featuredPosts?.length ? featuredPosts : latestNews}
                                heroSizes="(max-width: 1024px) 100vw, 66vw"
                                priority
                            />
                            {/* Promo slot 0 - fills remaining height */}
                            {promos[0] && (
                                <div className="mt-4 flex-1">
                                    <PromoSlot row={promos[0]} />
                                </div>
                            )}
                        </div>
                        <div>
                            {homeSlices.internationalGroups ? (
                                <InternationalStandingsCompact
                                    groups={homeSlices.internationalGroups}
                                    showHeader={false}
                                    groupsPerTab={3}
                                />
                            ) : (
                                <StandingsCompact standings={allStageStandings} showHeader={false} />
                            )}
                        </div>
                    </div>
                </SectionStrip>

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* Video Shorts */}
                {videoShortsSlot}

                {/* Row 3: Latest Results and Upcoming Matches */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                    <MatchTabbedList
                        finishedMatches={homeSlices.lastFinished}
                        upcomingMatches={homeSlices.nextUpcoming}
                    />
                </SectionStrip>

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* Video Highlights */}
                {videoHighlightsSlot}

                {/* Promo slot 1 */}
                <PromoSlot row={promos[1] ?? null} />

                {/* Row 5: Leaders (Top Scorers, Assisters, Goal Contributions) */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_LEADERS]}>
                    <LeadersTables leaders={leaders} />
                </SectionStrip>

                {/* Ad: mid-scroll-3 */}
                <AdSlot id="mid-scroll-3" />

                {/* Promo slot 2 */}
                <PromoSlot row={promos[2] ?? null} />

                {/* Row 6: Season Stats */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STATS]}>
                    {hasCompetitionStats && (
                        <SeasonStatsCards
                            competitionStats={competitionStats}
                            goalDistribution={goalDistribution}
                            teamWithMostWins={homeSlices.teamWithMostWins}
                        />
                    )}
                </SectionStrip>

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );
}
